import { HelpCircle, MapPinned, Minus, Plus, X } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useState } from "react";
import { View, XStack, YStack } from "tamagui";
import { useMap } from "../../../modules/Location/elements/Map";
import { PlacesAutoCompleteInput } from "../../../modules/Location/elements/PlacesAutoCompleteInput";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Label } from "../../../ui/Label";
import { LoadingCircular } from "../../../ui/LoadingCircular";
import { Sheet, type SheetProps } from "../../../ui/Sheet";
import { Title } from "../../../ui/Title";
import { CreateWizardButton } from "./CreateWizardButton";
import { CreateWizardProgress } from "./CreateWizardProgress";
import { type CreateWizardState, CreateWizardStep } from "./CreateWizardState";
import { Selection } from "./Selection";

const LocationPicker: React.FC<{ state: CreateWizardState }> = observer(
  (props) => {
    const { map, mapRef } = useMap();
    const [loading, setLoading] = useState(false);

    const handleSearchLocation = useCallback(
      async (location?: google.maps.places.PlacePrediction) => {
        if (!location) {
          return;
        }

        setLoading(true);

        const place = location.toPlace(); // Get first predicted place.
        await place.fetchFields({
          fields: ["displayName", "formattedAddress", "location"],
        });

        const placeLocation = place.location;
        if (placeLocation) {
          const mapData = {
            lat: placeLocation.lat(),
            lng: placeLocation.lng(),
          };

          props.state.setLocationModule({
            location: { title: place.displayName ?? "", latLng: mapData },
            skip: false,
          });
        }

        setLoading(false);
      },
      [props.state],
    );

    useEffect(() => {
      const location = props.state.locationModule?.location;

      if (map && location) {
        map.setItems([
          {
            label: location.title,
            mapData: location.latLng,
            planUid: "dummy",
            moduleUid: "dummy",
          },
        ]);

        setTimeout(() => {
          map.fitToMarkers();
        }, 100);
      }
    }, [map, props.state.locationModule]);

    return (
      <YStack gap="$small">
        {props.state.locationModule && (
          <View aspectRatio={5 / 2} width={"100%"} ref={mapRef} />
        )}

        {props.state.locationModule?.location ? (
          <XStack marginTop={"$small"} justifyContent="space-between">
            <Label>{props.state.locationModule.location.title}</Label>
            <View
              onPress={() => {
                map?.setItems([]);
                map?.fitToMarkers();
                props.state.setLocationModule();
              }}
            >
              <X />
            </View>
          </XStack>
        ) : (
          <PlacesAutoCompleteInput
            disabled={loading}
            placeholder={loading ? "Searching..." : "Search location..."}
            borderBottomWidth={1}
            blurOnSubmit={false}
            icon={loading ? () => <LoadingCircular /> : Plus}
            submitOnEnter
            type="border"
            onSelect={handleSearchLocation}
          />
        )}
      </YStack>
    );
  },
);

const MapSheetContinueButton: React.FC<{ state: CreateWizardState }> = observer(
  ({ state }) => {
    return (
      <CreateWizardButton
        onPress={() => state.setActiveStepId(CreateWizardStep.MODULES)}
        disabled={!state.locationModule?.location}
        subText={state.locationModule?.location?.title}
      >
        Continue
      </CreateWizardButton>
    );
  },
);

const MapSheet: React.FC<
  { state: CreateWizardState } & Omit<SheetProps, "sheetId">
> = observer(({ state, ...props }) => {
  return (
    <Sheet
      sheetId="mapSheet"
      header={<Title>Select location</Title>}
      footer={<MapSheetContinueButton state={state} />}
      {...props}
    >
      {props.open && <LocationPicker state={state} />}
    </Sheet>
  );
});

export const WizardStepLocation: React.FC<{
  state: CreateWizardState;
}> = observer((props) => {
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <YStack gap="$medium">
      <Box backgroundColor={"$primary"} gap={"$small"}>
        <Title>Do you know the location?</Title>
        <Body>
          Do you know the location for where this will take place, or does it
          even matter?
        </Body>

        <CreateWizardProgress state={props.state} />
      </Box>

      <Selection
        onSelect={() => {
          setSheetOpen(true);
        }}
        title={"Yes, we know the location!"}
        description="If you already know the location we'd love to know what it is to help you further"
        icon={MapPinned}
      />

      <Selection
        onSelect={() => {
          props.state.setLocationModule({ skip: false });
          props.state.setActiveStepId(CreateWizardStep.MODULES);
        }}
        title={"No, we don't know the location"}
        description="Planira will help you and your group find the perfect location for your happening"
        icon={HelpCircle}
      />

      <Selection
        onSelect={() => {
          props.state.setLocationModule({ skip: true });
          props.state.setActiveStepId(CreateWizardStep.MODULES);
        }}
        title={"Skip for now"}
        description="Planira can help you later on to find the perfect location"
        icon={Minus}
      />

      <MapSheet
        state={props.state}
        open={sheetOpen}
        onOpenChange={(open: boolean) => setSheetOpen(open)}
      />
    </YStack>
  );
});
